import {
  Alert,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { Link, Redirect } from "wouter";
import { useStore } from "./store";
import {
  IconUpload,
  IconWand,
  IconDownload,
  IconCoins,
} from "@tabler/icons-react";
import {
  destinationName,
  skusByKind,
  spendableSkus,
} from "./utils/skus";
import { useMarketplaces } from "./_Shared/useMarketplaces";
import MerchantHelp from "./Help/MerchantHelp";

function SkuBanner() {
  const tenantProfile = useStore((state) => state.tenantProfile);
  const { marketplaces } = useMarketplaces();
  if (!tenantProfile) return null;

  const skus = spendableSkus(tenantProfile.creditTotals);
  const hasBalance = (tenantProfile.creditTotals?.length ?? 0) > 0;
  const dest = destinationName(tenantProfile, marketplaces);
  const { sponsored, paid } = skusByKind(tenantProfile.creditTotals);

  if (hasBalance && skus === 0) {
    return (
      <Alert title="No SKUs left" color="red">
        Buy more SKUs to keep going.{" "}
        <Link to="/credits">Buy SKUs</Link>
      </Alert>
    );
  }

  if (sponsored > 0) {
    const from = dest || "your partner";
    return (
      <Alert
        title={`${sponsored.toLocaleString()} trial SKU${sponsored === 1 ? "" : "s"} from ${from}`}
        color={skus < 10 ? "orange" : "teal"}
      >
        {dest
          ? `They only pay for ${dest} listings.`
          : "They only pay for that marketplace."}
        {paid > 0
          ? ` You also have ${paid.toLocaleString()} paid SKU${paid === 1 ? "" : "s"} for any marketplace.`
          : ""}{" "}
        {skus < 10 && (
          <Link to="/credits">Buy more</Link>
        )}
      </Alert>
    );
  }

  if (hasBalance && skus < 10) {
    return (
      <Alert title="Low on SKUs" color="orange">
        You have {skus} SKU{skus === 1 ? "" : "s"} left.{" "}
        <Link to="/credits">Buy more</Link>
      </Alert>
    );
  }

  if (hasBalance) {
    return (
      <Text size="sm" c="dimmed">
        {skus.toLocaleString()} SKUs ready to use.
      </Text>
    );
  }

  return null;
}

function NextStep({ marketplaceName }) {
  const imports = useStore((state) => state.imports);
  const hasImports = Array.isArray(imports) && imports.length > 0;
  const hasOptivised =
    Array.isArray(imports) &&
    imports.some(
      (imp) =>
        imp.status === "optivised" || imp.status === "partiallyoptivised",
    );
  const dest = marketplaceName || "the marketplace";

  if (!hasImports) {
    return (
      <Paper p="xl" withBorder>
        <Title order={3}>Upload your catalogue</Title>
        <Text c="dimmed" mt="sm" mb="lg">
          Upload a spreadsheet from your webshop, another marketplace like
          eBay, your supplier, or your Amazon ASINs. We detect the file type.
          Destination is {dest}.
        </Text>
        <Button
          component={Link}
          to="/imports/upload"
          leftSection={<IconUpload size={16} />}
        >
          Upload a file
        </Button>
      </Paper>
    );
  }

  if (!hasOptivised) {
    return (
      <Paper p="xl" withBorder>
        <Title order={3}>Review and Optivise</Title>
        <Text c="dimmed" mt="sm" mb="lg">
          Check the file looks right, then Optivise for {dest}.
        </Text>
        <Button
          component={Link}
          to="/imports"
          leftSection={<IconWand size={16} />}
        >
          Open my file
        </Button>
      </Paper>
    );
  }

  return (
    <Paper p="xl" withBorder>
      <Title order={3}>Download and publish</Title>
      <Text c="dimmed" mt="sm" mb="lg">
        Download the file and upload it in your own {dest} back office.
      </Text>
      <Group>
        <Button
          component={Link}
          to="/imports"
          leftSection={<IconDownload size={16} />}
        >
          Go to my file
        </Button>
        <Button
          component={Link}
          to="/imports/upload"
          variant="default"
          leftSection={<IconUpload size={16} />}
        >
          Upload another file
        </Button>
      </Group>
    </Paper>
  );
}

export default function Home() {
  const authToken = useStore((state) => state.authToken);
  const profile = useStore((state) => state.profile);
  const tenantProfile = useStore((state) => state.tenantProfile);
  const { marketplaces } = useMarketplaces();

  if (!authToken) {
    return <Redirect to="/auth/login" />;
  }
  if (profile?.role === "MarketplacePartner") {
    return <Redirect to="/partner" />;
  }

  const partnerMpId = tenantProfile?.partnerMarketplaceId;
  const marketplaceName =
    marketplaces?.find((m) => m.value === partnerMpId)?.label ||
    (partnerMpId ? "B&Q Marketplace" : null);

  return (
    <Container size="xl">
      <Stack gap="lg">
        <div>
          <Title order={1} mt="md">
            {marketplaceName
              ? `Get a complete catalogue live on ${marketplaceName}`
              : "Get a complete catalogue live"}
          </Title>
          <Text c="dimmed" mt={6}>
            Upload, review, download. You publish in your own back office.
          </Text>
        </div>

        <SkuBanner />
        <NextStep marketplaceName={marketplaceName} />
        <MerchantHelp marketplaceName={marketplaceName} />

        {spendableSkus(tenantProfile?.creditTotals) === 0 &&
          (tenantProfile?.creditTotals?.length ?? 0) > 0 && (
            <Button
              component={Link}
              to="/credits"
              variant="subtle"
              leftSection={<IconCoins size={16} />}
            >
              Buy SKUs
            </Button>
          )}
      </Stack>
    </Container>
  );
}
